/**
 * Application Entry Point
 *
 * Initializes React, React Query, and mounts the root component.
 */

// Polyfill URL.parse for Electron (Chromium 120 doesn't have it, added in Chrome 126)
// This must be before any imports that use react-pdf/pdfjs-dist
if (typeof URL.parse !== 'function') {
  URL.parse = function (url: string, base?: string | URL): URL | null {
    try {
      return new URL(url, base);
    } catch {
      return null;
    }
  };
}

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';


import App from './app';
import './globals.css';
import { ThemeProvider } from './shared/components/context/theme-provider';
import { TooltipProvider } from './shared/components/ui/tooltip';

// Create a React Query client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default stale time (hooks override per data volatility)
      staleTime: 5 * 60 * 1000,
      // Cache time: how long inactive data stays in cache (10 minutes)
      gcTime: 10 * 60 * 1000,
      // Retry once — avoids 10s+ delay before showing errors (3 retries × ~3s backoff)
      retry: 1,
      // Refetch on window focus for fresh data
      refetchOnWindowFocus: true,
    },
    mutations: {
      // Never retry mutations — financial operations are not idempotent,
      // and a retry after a timeout could create duplicate records.
      retry: 0,
    }
  },
});

// Mount the application
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="system">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={300}>
          <App />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>
);
